// ProfileSettings.js
import React, { useEffect, useState } from 'react';
import API from './api';

const defaultPrefs = {
  push_telehealth_reminders: true,
  push_visit_changes: true,
  push_messages: true,
  email_summary_reports: false,
};

function ProfileSettings() {
  const [user, setUser] = useState(null);
  const [prefs, setPrefs] = useState(defaultPrefs);
  const [loadingPrefs, setLoadingPrefs] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      if (stored && stored.id) {
        setUser(stored);
      }
    } catch (e) {
      console.error('Failed to parse user from localStorage', e);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    async function loadPrefs() {
      setLoadingPrefs(true);
      setSaveMessage(null);
      setSaveError(null);
      try {
        const res = await API.get('/me/notification-preferences');
        if (!mounted) return;
        const serverPrefs = res.data || {};
        setPrefs({
          ...defaultPrefs,
          ...serverPrefs,
        });
      } catch (err) {
        // Ako endpoint još ne postoji ili vrati grešku – koristimo default
        console.warn('Notification prefs load failed, using defaults', err?.response?.status || err?.message);
        if (!mounted) return;
        setPrefs(defaultPrefs);
      } finally {
        if (mounted) setLoadingPrefs(false);
      }
    }

    loadPrefs();
    return () => {
      mounted = false;
    };
  }, []);

  const handleToggle = (key) => {
    setPrefs((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
    setSaveMessage(null);
    setSaveError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage(null);
    setSaveError(null);
    try {
      await API.post('/me/notification-preferences', prefs);
      setSaveMessage('Notification preferences saved.');
    } catch (err) {
      console.error('Failed to save notification prefs', err);
      if (err?.response?.status === 404) {
        setSaveError('Saving failed: backend endpoint /me/notification-preferences not implemented yet.');
      } else {
        setSaveError('Saving failed. Please try again or contact your administrator.');
      }
    } finally {
      setSaving(false);
    }
  };

  const roleLabel = (role) => {
    if (!role) return '';
    if (role === 'teacher') return 'Provider';
    if (role === 'parent') return 'Parent';
    if (role === 'admin') return 'Admin';
    return role;
  };

  return (
    <div className="w-full max-w-3xl mx-auto px-4 py-6">
      <h2 className="text-xl font-bold mb-4">Profile &amp; Notifications</h2>

      {/* Profile card */}
      <div className="bg-white shadow rounded-xl border mb-6">
        <div className="border-b px-4 py-3 bg-gray-50 rounded-t-xl">
          <h3 className="font-semibold text-gray-800 text-sm">Profile</h3>
          <p className="text-xs text-gray-500 mt-1">
            Your account is managed by your organization. For any changes to your personal information,
            please contact your administrator or provider.
          </p>
        </div>
        <div className="px-4 py-4">
          {user ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">Name</div>
                <div className="font-medium text-gray-900">{user.name || '-'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">Email</div>
                <div className="font-mono text-gray-900 break-all">{user.email || '-'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">Role</div>
                <div className="font-medium text-gray-900">
                  {roleLabel(user.role) || '-'}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">Organization</div>
                <div className="font-medium text-gray-900">
                  {user.organization_name || user.organization_slug || user.organization_id || '-'}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-500">
              Unable to load profile from this device. Try logging out and logging back in.
            </div>
          )}
        </div>
      </div>

      {/* Notification preferences */}
      <div className="bg-white shadow rounded-xl border">
        <div className="border-b px-4 py-3 bg-gray-50 rounded-t-xl">
          <h3 className="font-semibold text-gray-800 text-sm">Notification preferences</h3>
          <p className="text-xs text-gray-500 mt-1">
            These settings apply to this account. Some notifications may still be sent if required for
            important schedule updates.
          </p>
        </div>
        <div className="px-4 py-4">
          {loadingPrefs ? (
            <div className="text-sm text-gray-500">Loading preferences...</div>
          ) : (
            <div className="space-y-4 text-sm">
              <div className="flex items-start gap-3">
                <input
                  id="push_telehealth_reminders"
                  type="checkbox"
                  className="mt-1 h-4 w-4"
                  checked={!!prefs.push_telehealth_reminders}
                  onChange={() => handleToggle('push_telehealth_reminders')}
                />
                <label htmlFor="push_telehealth_reminders" className="cursor-pointer">
                  <div className="font-medium text-gray-900">
                    Telehealth session reminders
                  </div>
                  <div className="text-xs text-gray-500">
                    Receive push notifications about telehealth sessions starting soon (e.g. 15 minutes
                    before the session).
                  </div>
                </label>
              </div>

              <div className="flex items-start gap-3">
                <input
                  id="push_visit_changes"
                  type="checkbox"
                  className="mt-1 h-4 w-4"
                  checked={!!prefs.push_visit_changes}
                  onChange={() => handleToggle('push_visit_changes')}
                />
                <label htmlFor="push_visit_changes" className="cursor-pointer">
                  <div className="font-medium text-gray-900">
                    Visit changes &amp; cancellations
                  </div>
                  <div className="text-xs text-gray-500">
                    Get notified when a visit is rescheduled or canceled.
                  </div>
                </label>
              </div>

              <div className="flex items-start gap-3">
                <input
                  id="push_messages"
                  type="checkbox"
                  className="mt-1 h-4 w-4"
                  checked={!!prefs.push_messages}
                  onChange={() => handleToggle('push_messages')}
                />
                <label htmlFor="push_messages" className="cursor-pointer">
                  <div className="font-medium text-gray-900">
                    Messages &amp; updates
                  </div>
                  <div className="text-xs text-gray-500">
                    Push notifications when you receive new messages or important updates from your
                    organization.
                  </div>
                </label>
              </div>

              <div className="flex items-start gap-3">
                <input
                  id="email_summary_reports"
                  type="checkbox"
                  className="mt-1 h-4 w-4"
                  checked={!!prefs.email_summary_reports}
                  onChange={() => handleToggle('email_summary_reports')}
                />
                <label htmlFor="email_summary_reports" className="cursor-pointer">
                  <div className="font-medium text-gray-900">
                    Email summaries (where available)
                  </div>
                  <div className="text-xs text-gray-500">
                    Receive occasional summary emails (for example, visit or progress summaries), if enabled
                    by your organization.
                  </div>
                </label>
              </div>
            </div>
          )}

          <div className="mt-5 flex items-center justify-between gap-4">
            <div className="text-xs text-gray-400">
              Changes may take a few moments to apply across all devices.
            </div>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || loadingPrefs}
              className={`px-4 py-2 rounded-lg text-sm font-semibold ${
                saving || loadingPrefs
                  ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {saving ? 'Saving...' : 'Save preferences'}
            </button>
          </div>

          {saveMessage && (
            <div className="mt-3 text-xs text-green-600">
              {saveMessage}
            </div>
          )}
          {saveError && (
            <div className="mt-3 text-xs text-red-600">
              {saveError}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfileSettings;

